/**
 * Inline review composer for the Judge.me product widget.
 *
 * Judge.me's current widget only offers a "Write a review" button that opens a
 * multi-step modal. Shoppers on this store wanted to start writing straight on
 * the page, so this renders stars, a text box and a photo picker inline, and on
 * "Post review" hands everything to Judge.me's own modal: it opens the modal,
 * presses the matching star, fills the review body and attaches the photos.
 * Judge.me still owns submission (name, email, spam checks, verification).
 *
 * Every hand-off is best-effort. If Judge.me's markup is not found, the composer
 * never mounts and the original button stays exactly as it was.
 */

const WIDGET = '#judgeme_product_reviews';
const OPEN_BUTTON = '.jm-action-buttons__button';
const MODAL = '.jdgm-write-review-modal';
const BODY_INPUT = '#jdgm-review-body-input';
const MEDIA_INPUT = 'input.jdgm-write-review-modal__media-input';
const MODAL_STAR = (n) => `${MODAL} [aria-label="${n} star${n === 1 ? '' : 's'}"]`;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const isVisible = (element) => Boolean(element) && element.getBoundingClientRect().height > 0;

async function waitFor(find, timeout = 10000, step = 200) {
  for (let elapsed = 0; elapsed <= timeout; elapsed += step) {
    const found = find();
    if (found) return found;
    await wait(step);
  }
  return null;
}

/** Sets a value the way a typing user would, so framework-managed inputs notice. */
function setNativeValue(element, value) {
  const setter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(element), 'value')?.set;
  if (setter) setter.call(element, value);
  else element.value = value;
  element.dispatchEvent(new Event('input', { bubbles: true }));
  element.dispatchEvent(new Event('change', { bubbles: true }));
}

class ArtzoneReviewComposer extends HTMLElement {
  #rating = 0;
  #started = false;

  async connectedCallback() {
    // Moving this element into the Judge.me widget below re-runs connectedCallback.
    // Without this guard that re-run moves it again, forever, and freezes the page.
    if (this.#started) return;
    this.#started = true;

    const openButton = await waitFor(() => document.querySelector(`${WIDGET} ${OPEN_BUTTON}`));
    if (!openButton) return;

    this.stars = [...this.querySelectorAll('.artzone-review-composer__star')];
    this.textarea = this.querySelector('textarea');
    this.fileInput = this.querySelector('input[type="file"]');
    this.fileLabel = this.querySelector('.artzone-review-composer__files');
    this.postButton = this.querySelector('.artzone-review-composer__post');

    // Sit where Judge.me's own button was, inside its widget, under its header.
    openButton.closest('.jm-action-buttons, .jm-cluster, div')?.after(this);
    this.hidden = false;
    document.querySelector(WIDGET)?.classList.add('artzone-review-composer-ready');

    this.stars.forEach((star) => star.addEventListener('click', () => this.#setRating(Number(star.dataset.value))));
    this.fileInput.addEventListener('change', () => this.#showFiles());
    this.postButton.addEventListener('click', () => this.#handOff());
  }

  #setRating(value) {
    this.#rating = value;
    this.stars.forEach((star) => {
      star.classList.toggle('is-on', Number(star.dataset.value) <= value);
      star.setAttribute('aria-pressed', String(Number(star.dataset.value) === value));
    });
  }

  #showFiles() {
    const count = this.fileInput.files.length;
    this.fileLabel.textContent = count ? `${count} photo${count === 1 ? '' : 's'} added` : '';
  }

  async #handOff() {
    const text = this.textarea.value.trim();
    const files = [...this.fileInput.files];
    // Judge.me opens the modal by setting display:block on it straight away and then
    // fading it in. Read display, not its on-screen size: the fade can be mid-way
    // (or paused in a background tab) while the modal is already open.
    const modalOpen = () => [...document.querySelectorAll(MODAL)].some((m) => getComputedStyle(m).display !== 'none');

    // Judge.me wires its button up a moment after it renders, so an early click can
    // land before anything listens. Retry until the modal is genuinely on screen.
    for (let attempt = 0; attempt < 4 && !modalOpen(); attempt++) {
      document.querySelector(`${WIDGET} ${OPEN_BUTTON}`)?.click();
      await waitFor(modalOpen, 1500, 100);
    }
    if (!modalOpen()) return;

    // Let the fade-in finish first. Clicking inside the modal while it is still
    // animating open makes Judge.me treat the click as outside it and close it.
    const settled = () =>
      [...document.querySelectorAll(MODAL)].some(
        (m) => getComputedStyle(m).display !== 'none' && Number(getComputedStyle(m).opacity) >= 0.99
      );
    await waitFor(settled, 2500, 100);
    await wait(150);

    if (this.#rating) {
      // The modal holds several star groups (one per step); press the one showing.
      const star = await waitFor(
        () => [...document.querySelectorAll(MODAL_STAR(this.#rating))].find(isVisible),
        3000
      );
      star?.click();
      await wait(400);
    }

    // Judge.me resets the review body when the modal opens and when it changes
    // step, so fill it only once the field is on screen, and confirm it stuck.
    if (text) {
      for (let attempt = 0; attempt < 5; attempt++) {
        const body = await waitFor(() => [...document.querySelectorAll(BODY_INPUT)].find(isVisible), 2000);
        if (!body) break;
        if (body.value !== text) setNativeValue(body, text);
        await wait(250);
        if (body.value === text) break;
      }
    }

    if (files.length) {
      const media = document.querySelector(MEDIA_INPUT);
      if (media) {
        try {
          const transfer = new DataTransfer();
          files.forEach((file) => transfer.items.add(file));
          media.files = transfer.files;
          media.dispatchEvent(new Event('change', { bubbles: true }));
        } catch (error) {
          // Older browsers cannot assign a FileList; the shopper re-picks in the modal.
        }
      }
    }
  }
}

if (!customElements.get('artzone-review-composer')) {
  customElements.define('artzone-review-composer', ArtzoneReviewComposer);
}
